import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL;

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [sort]);

  const fetchBlogs = async (searchText) => {
    setLoading(true);
    try {
      const q = searchText !== undefined ? searchText : search;
      const res = await fetch(`${API}/api/blogs?search=${encodeURIComponent(q)}&sort=${sort}`);
      const data = await res.json();
      setBlogs(data.blogs || []);
    } catch (e) {
      console.error("Failed to fetch blogs:", e);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBlogs(search);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric", month: "long", year: "numeric"
      });
    } catch { return ""; }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div data-testid="blogs-page" style={{ minHeight: "80vh", background: "#FAF7F3", padding: "40px 0" }}>
      <Helmet>
        <title>Blogs | Vedic Astrology, Spirituality & Life Wisdom | Amatya</title>
        <meta name="description" content="Read Amatya's blogs on Vedic astrology, kundli analysis, yagya, hawan, spirituality, and life wisdom. Insights in English and Hindi." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.amatya.co.in/blogs" />
        <meta property="og:title" content="Blogs — Vedic Astrology & Spirituality | Amatya" />
        <meta property="og:description" content="Vedic astrology insights, spiritual wisdom, and life guidance from Amatya." />
        <meta property="og:url" content="https://www.amatya.co.in/blogs" />
      </Helmet>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
        <h1 data-testid="blogs-title" style={{
          fontFamily: "Cinzel, serif", fontSize: "2rem", color: "#2E221A",
          textAlign: "center", marginBottom: 8
        }}>
          Blogs
        </h1>
        <p style={{
          fontFamily: "IM Fell English, serif", fontSize: "1.1rem", color: "#725b47",
          textAlign: "center", marginBottom: 32
        }}>
          Vedic wisdom, spiritual insights, and honest guidance
        </p>

        {/* Search & Sort */}
        <div data-testid="blogs-controls" style={{
          display: "flex", gap: 12, marginBottom: 32, flexWrap: "wrap", justifyContent: "center"
        }}>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, flex: "1 1 300px", maxWidth: 500 }}>
            <input
              data-testid="blog-search-input"
              type="text"
              placeholder="Search blogs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1, padding: "10px 16px", border: "1px solid #d4c5b0",
                borderRadius: 6, fontFamily: "Lato, sans-serif", fontSize: "0.95rem",
                background: "#fff", outline: "none"
              }}
            />
            <button
              data-testid="blog-search-btn"
              type="submit"
              style={{
                padding: "10px 20px", background: "#8B4513", color: "#fff",
                border: "none", borderRadius: 6, cursor: "pointer",
                fontFamily: "Lato, sans-serif", fontSize: "0.9rem"
              }}
            >
              Search
            </button>
          </form>
          <select
            data-testid="blog-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{
              padding: "10px 16px", border: "1px solid #d4c5b0", borderRadius: 6,
              fontFamily: "Lato, sans-serif", fontSize: "0.9rem", background: "#fff", cursor: "pointer"
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {/* Blog List */}
        {loading ? (
          <p style={{ textAlign: "center", color: "#725b47", fontFamily: "Lato, sans-serif" }}>Loading blogs...</p>
        ) : blogs.length === 0 ? (
          <div data-testid="no-blogs" style={{
            textAlign: "center", padding: 60, color: "#725b47",
            fontFamily: "IM Fell English, serif", fontSize: "1.1rem"
          }}>
            {search ? "No blogs found for your search." : "No blogs published yet. Check back soon!"}
          </div>
        ) : (
          <div data-testid="blog-list" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {blogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blogs/${blog.slug}`}
                data-testid={`blog-card-${blog.slug}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <article style={{
                  background: "#fff", borderRadius: 10, overflow: "hidden",
                  border: "1px solid #e8ddd0", transition: "box-shadow 0.3s",
                  display: "flex", flexDirection: blog.featured_image ? "row" : "column",
                  minHeight: blog.featured_image ? 180 : "auto"
                }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 20px rgba(46,34,26,0.12)"}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}
                >
                  {blog.featured_image && (
                    <img
                      src={blog.featured_image}
                      alt={blog.title}
                      className="blog-card-image"
                      loading="lazy"
                    />
                  )}
                  <div style={{ padding: "20px 24px", flex: 1 }}>
                    <h2 style={{
                      fontFamily: "Cinzel, serif", fontSize: "1.2rem", color: "#2E221A",
                      margin: "0 0 8px", lineHeight: 1.3
                    }}>
                      {blog.title}
                    </h2>
                    <div style={{
                      display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap",
                      fontFamily: "Lato, sans-serif", fontSize: "0.8rem", color: "#9a8572"
                    }}>
                      <span>{formatDate(blog.published_at)}</span>
                      {blog.author && <span>By {blog.author}</span>}
                    </div>
                    <p style={{
                      fontFamily: "Lato, sans-serif", fontSize: "0.95rem", color: "#5a4a3a",
                      lineHeight: 1.6, margin: 0,
                      display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden"
                    }}>
                      {blog.excerpt || stripHtml(blog.content).substring(0, 200)}
                    </p>
                    {blog.tags && blog.tags.length > 0 && (
                      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                        {blog.tags.map((tag, i) => (
                          <span key={i} style={{
                            padding: "3px 10px", background: "#f5efe8", borderRadius: 12,
                            fontSize: "0.75rem", color: "#8B4513", fontFamily: "Lato, sans-serif"
                          }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsPage;
